import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import viewImage from "../../assets/images/icons/view.svg";
import FilterTrigger from "../../commons/FilterTrigger/FilterTrigger";
import FilterOptionsMenu from "../../commons/FilterOptionsMenu/FilterOptionsMenu";
import DateRangeSelectButton from "../../commons/DateRangeSelectButton/DateRangeSelectButton";
import TablePagination from "../../commons/Pagination/TablePagination";
import CopyableId from "../../commons/CopyableId/CopyableId";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";

const STATUS_FILTER_OPTIONS = [
  "All",
  "Pending",
  "Processing",
  "Completed",
  "Failed",
];

const STATUS_LABELS = {
  0: "Pending",
  1: "Processing",
  2: "Completed",
  3: "Failed",
};

const STATUS_FILTER_MAP = {
  Pending: 0,
  Processing: 1,
  Completed: 2,
  Failed: 3,
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `$${num.toFixed(2)}`;
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatStatus = (status) => {
  if (status === null || status === undefined || status === "") return "N/A";
  if (STATUS_LABELS[status] !== undefined) return STATUS_LABELS[status];
  return String(status);
};

const mapPayoutPartyRow = (item) => {
  const restaurant = item?.restaurant_id;
  const driver = item?.driver_id;
  const party = restaurant || driver || {};

  const type = restaurant ? "Restaurant" : driver ? "Driver" : "N/A";

  const name =
    restaurant?.restaurant_name ||
    restaurant?.full_name ||
    driver?.full_name ||
    driver?.name ||
    "N/A";

  const phone = party?.phone
    ? `${party?.country_code ? `+${party.country_code} ` : ""}${party.phone}`
    : "N/A";

  return {
    id: item?._id,
    transactionRefId: item?.transaction_ref_id || item?.transaction_id || "N/A",
    name,
    type,
    phone,
    email: party?.email || "N/A",
    orders: item?.total_order ?? "N/A",
    gross: formatMoney(item?.gross_amount),
    tax: formatMoney(item?.tax_amount),
    commission: formatMoney(item?.platform_commission),
    netPayable: formatMoney(item?.net_payable_amount),
    status: formatStatus(item?.status_str || item?.status),
    created: formatDate(item?.createdAt),
    raw: item,
  };
};

const parsePayoutPartyList = (payload) => {
  const list = payload?.payout_restaurant;
  return Array.isArray(list) ? list.map(mapPayoutPartyRow) : [];
};

const getStatusBadgeClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "processing") return "badge-info";
  if (normalized === "pending") return "badge-warning";
  if (normalized === "completed" || normalized === "success" || normalized === "paid") {
    return "badge-success";
  }
  if (normalized === "failed" || normalized === "rejected") return "badge-danger";
  return "badge-secondary";
};

function ScheduledPayoutRestaurants() {
  const { payoutId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const payoutRefId = location.state?.payoutRefId;
  const settlementPeriod = location.state?.settlementPeriod;

  const handleViewTransaction = (item) => {
    if (!item?.id) return;
    navigate(`/payout-management/scheduled-payouts/${payoutId}/transaction/${item.id}`, {
      state: {
        payoutRefId,
        settlementPeriod,
        transaction: item.raw || item,
      },
    });
  };

  const [statusFilter, setStatusFilter] = useState("All");
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [fromdate, setFromdate] = useState(null);
  const [todate, setTodate] = useState(null);
  const [showAppliedRange, setShowAppliedRange] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const limit = 10;

  const statusFilterRef = useRef(null);

  const fetchRestaurants = useCallback(async () => {
    if (!payoutId) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        payout_id: payoutId,
      });

      if (statusFilter !== "All" && STATUS_FILTER_MAP[statusFilter] !== undefined) {
        params.set("status", String(STATUS_FILTER_MAP[statusFilter]));
      }

      if (fromdate && todate) {
        params.set("fromdate", fromdate);
        params.set("todate", todate);
      }

      const response = await handleApiCall(
        "get",
        `${endpoints.listOfPayoutRestaurant}?${params.toString()}`
      );

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const payload = response?.data?.data;
        setRows(parsePayoutPartyList(payload));
        setTotalPages(
          Number(
            payload?.pagination?.total_pages ||
              payload?.pagination?.totalPages ||
              payload?.totalPages ||
              payload?.total_pages ||
              1
          ) || 1
        );
      } else {
        setRows([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching payout restaurants:", error);
      setRows([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [payoutId, page, limit, statusFilter, fromdate, todate]);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setShowStatusFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleApplyDateRange = (start, end) => {
    setFromdate(start);
    setTodate(end);
    setShowAppliedRange(true);
    setPage(1);
  };

  const handleResetDateRange = () => {
    setFromdate(null);
    setTodate(null);
    setShowAppliedRange(false);
    setPage(1);
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-4 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">
                Payout Details
                {payoutRefId ? (
                  <span className="text-muted ml-2" style={{ fontSize: "0.9rem" }}>
                    ({payoutRefId})
                  </span>
                ) : null}
              </h3>
              <div className="ms-auto">
                <button
                  type="button"
                  className="btn btn-default rounded-lg"
                  onClick={() => navigate("/payout-management/scheduled-payouts")}
                >
                  Back
                </button>
              </div>
            </div>

            {showAppliedRange && fromdate && todate && (
              <p className="text-muted small mb-3">
                Showing data from {fromdate} to {todate}
              </p>
            )}

            <div className="mb-4 px-0 py-2">
              <div className="card-heading d-flex align-items-center mb-3 pb-2">
                <div className="d-flex align-items-center col-md-12 px-0 flex-wrap">
                  <div className="d-flex align-items-center ml-auto mt-2 mt-md-0 flex-nowrap">
                    <div
                      ref={statusFilterRef}
                      className="d-flex align-items-center position-relative mr-3"
                    >
                      <FilterTrigger
                        active={statusFilter !== "All"}
                        onClick={() => setShowStatusFilter((prev) => !prev)}
                      >
                        {showStatusFilter && (
                          <FilterOptionsMenu
                            options={STATUS_FILTER_OPTIONS}
                            value={statusFilter}
                            style={{ right: 0, top: "calc(100% + 8px)" }}
                            onSelect={(option) => {
                              setStatusFilter(option);
                              setShowStatusFilter(false);
                              setPage(1);
                            }}
                          />
                        )}
                      </FilterTrigger>
                      <span
                        className="ml-2 text-white"
                        role="button"
                        onClick={() => setShowStatusFilter((prev) => !prev)}
                      >
                        {statusFilter === "All" ? "All statuses" : statusFilter}
                      </span>
                    </div>

                    <DateRangeSelectButton
                      appliedFrom={fromdate}
                      appliedTo={todate}
                      onApply={handleApplyDateRange}
                      onReset={handleResetDateRange}
                    />
                  </div>
                </div>
              </div>

              <div className="col-md-12 tab-content row px-0 mx-0">
                <div className="table-responsive px-0">
                  <table className="table table-striped trim">
                    <thead>
                      <tr>
                        <th>Transaction ID</th>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Phone</th>
                        <th>Orders</th>
                        <th>Gross</th>
                        <th>Tax</th>
                        <th>Commission</th>
                        <th>Net Payable</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="12" className="text-center py-4">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">
                                {UI_CONSTANTS.BUTTON_LABELS.LOADING}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : rows.length > 0 ? (
                        rows.map((item) => (
                          <tr key={item.id}>
                            <td className="text-left">
                              <CopyableId value={item.transactionRefId} />
                            </td>
                            <td>{item.name}</td>
                            <td>{item.type}</td>
                            <td>{item.phone}</td>
                            <td>{item.orders}</td>
                            <td>{item.gross}</td>
                            <td>{item.tax}</td>
                            <td>{item.commission}</td>
                            <td>{item.netPayable}</td>
                            <td>
                              <span className={`badge ${getStatusBadgeClass(item.status)}`}>
                                {item.status}
                              </span>
                            </td>
                            <td>{item.created}</td>
                            <td>
                              <div className="d-flex align-items-center justify-content-center">
                                <button
                                  type="button"
                                  className="btn btn-link p-0 mx-2"
                                  title="View"
                                  onClick={() => handleViewTransaction(item)}
                                >
                                  <img src={viewImage} alt="View" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="12" className="text-center py-4">
                            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScheduledPayoutRestaurants;
